import * as Api from '@ViewModels';
import { useInfiniteQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { AIAgentTemplateFilter, IAIAgentTemplate } from '../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = '/aiagenttemplate/filter';

const getAIAgentTemplateFilterInfiniteQueryOptions = (
	{
		filter,
		pageSize = 25,
		impersonationContext,
	}: {
		filter: AIAgentTemplateFilter;
		pageSize?: number;
		impersonationContext?: Api.IImpersonationContext;
	},
	userSession: ReturnType<typeof useUserSession>
) => ({
	queryFn: ({ pageParam }: { pageParam: unknown }) => {
		const pageToken = typeof pageParam === 'string' ? pageParam : '';
		return userSession.webServiceHelper.callAsync<Api.IPagedCollection<IAIAgentTemplate>>(
			Api.ImpersonationBroker.composeApiUrl({
				impersonationContext,
				queryParams: { pageSize, pageToken },
				urlPath: 'aiagenttemplate/filter',
			}),
			'POST',
			filter
		);
	},
	getNextPageParam: (lastPage: Api.IPagedCollection<IAIAgentTemplate>) => lastPage.pageToken,
	queryKey: [BASE_QUERY_KEY, filter, pageSize, impersonationContext],
	initialPageParam: '',
});

export const useAIAgentTemplateFilterInfiniteQuery = ({
	filter,
	pageSize = 25,
	enabled = true,
	impersonationContext,
}: {
	filter: AIAgentTemplateFilter;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useInfiniteQuery({
		...getAIAgentTemplateFilterInfiniteQueryOptions({ filter, pageSize, impersonationContext }, userSession),
		enabled,
	});
};

export const useAIAgentTemplateFilterInfiniteSuspenseQuery = ({
	filter,
	pageSize = 25,
	impersonationContext,
}: {
	filter: AIAgentTemplateFilter;
	pageSize?: number;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useSuspenseInfiniteQuery(
		getAIAgentTemplateFilterInfiniteQueryOptions({ filter, pageSize, impersonationContext }, userSession)
	);
};

export const invalidateAIAgentTemplateFilter = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
